import { makeStyles } from '@material-ui/core';
import { BoxSizingProperty, TextAlignProperty } from 'csstype';
import React, { FunctionComponent, ReactNode } from 'react';
import MediaQuery from 'react-responsive';
import { DOCUMENT_MODAL_HEADER_HEIGHT } from './DocumentModal';

const useStyles = makeStyles({
	documentWrapper: (props: DocumentBreakpointWrapperProps) => ({
		width: '100%',
		height: `calc(100% - ${props.modalHeaderHeight})`,
		border: '1px solid #aaa',
		overflow: 'scroll',
		boxSizing: 'border-box' as BoxSizingProperty,
		marginTop: '15px',
		padding: '8px',
		// basic styling for contentful rich text
		fontSize: '0.7em',
		textAlign: 'left' as TextAlignProperty,
		'& p': {
			lineHeight: '1.2em',
			color: '#333',
			fontWeight: 400,
		},
	}),
});

interface DocumentBreakpointWrapperProps {
	mobileDocument?: ReactNode;
	desktopIframeSrc?: string;
	title?: string;
	breakpoint?: number;
	modalHeaderHeight?: string;
}

/**
 * A breakpoint wrapper for legal documents. Provides a mobile view that displays rich text and a
 * desktop view that displays an iframe (usually containing a PDF viewer)
 * @param mobileDocument the mobile document
 * @param desktopIframeSrc the desktop iframe source
 * @param title the document title
 * @param breakpoint the breakpoint when to switch between mobile and desktop view
 * @param modalHeaderHeight height of the modal header above the document
 */
export const DocumentBreakpointWrapper: FunctionComponent<DocumentBreakpointWrapperProps> =
	({
		mobileDocument,
		desktopIframeSrc,
		title = '',
		breakpoint = 960,
		modalHeaderHeight = DOCUMENT_MODAL_HEADER_HEIGHT,
	}) => {
		const classes = useStyles({ modalHeaderHeight });

		const mobileDoc = (
			<div className={classes.documentWrapper}>{mobileDocument}</div>
		);
		const mobileWrapper =
			!!mobileDocument && !!desktopIframeSrc ? (
				<MediaQuery maxWidth={breakpoint - 1}>{mobileDoc}</MediaQuery>
			) : !!mobileDocument ? (
				mobileDoc
			) : null;

		const desktopDoc = (
			<iframe
				title={title}
				className={classes.documentWrapper}
				src={desktopIframeSrc}
			/>
		);
		const desktopWrapper =
			!!mobileDocument && !!desktopIframeSrc ? (
				<MediaQuery minWidth={breakpoint}>{desktopDoc}</MediaQuery>
			) : !!desktopIframeSrc ? (
				desktopDoc
			) : null;

		return (
			<>
				{mobileWrapper}
				{desktopWrapper}
			</>
		);
	};
